<template>
    <aside
        ref="root"
        class="preferred-source-cta"
        aria-labelledby="preferred-source-title"
    >
        <div class="preferred-source-cta__copy">
            <h2
                id="preferred-source-title"
                class="preferred-source-cta__title k-heading-4"
            >
                See more from {{ publicationName }} in Google
            </h2>
            <p class="preferred-source-cta__text">
                Add us as a preferred source to make our local travel guides easier to find.
            </p>
        </div>

        <div class="preferred-source-cta__control">
            <button
                v-if="!hasFailed"
                type="button"
                class="preferred-source-cta__google-button"
                :disabled="!isReady"
                :aria-busy="!isReady"
                @click="openPreferredSources"
            >
                <svg
                    class="preferred-source-cta__google-icon"
                    aria-hidden="true"
                    viewBox="0 0 18 18"
                >
                    <path
                        fill="#4285f4"
                        d="M17.64 9.205c0-.638-.057-1.252-.164-1.841H9v3.481h4.844a4.14 4.14 0 0 1-1.797 2.715v2.259h2.909c1.702-1.567 2.684-3.874 2.684-6.614Z"
                    />
                    <path
                        fill="#34a853"
                        d="M9 18c2.43 0 4.467-.806 5.956-2.181l-2.909-2.259c-.806.54-1.836.859-3.047.859-2.344 0-4.328-1.585-5.037-3.715H.956v2.332A9 9 0 0 0 9 18Z"
                    />
                    <path
                        fill="#fbbc05"
                        d="M3.963 10.704A5.41 5.41 0 0 1 3.682 9c0-.592.102-1.167.281-1.704V4.964H.956A9 9 0 0 0 0 9c0 1.452.347 2.827.956 4.036l3.007-2.332Z"
                    />
                    <path
                        fill="#ea4335"
                        d="M9 3.581c1.321 0 2.507.454 3.441 1.346l2.581-2.581C13.463.892 11.426 0 9 0A9 9 0 0 0 .956 4.964l3.007 2.332C4.672 5.166 6.656 3.581 9 3.581Z"
                    />
                </svg>
                <span>Add as a preferred source on Google</span>
            </button>

            <a
                v-if="hasFailed"
                class="preferred-source-cta__google-button"
                :href="deeplink"
                target="_blank"
                rel="noopener noreferrer"
                @click="emit('fallbackClick')"
            >
                <svg
                    class="preferred-source-cta__google-icon"
                    aria-hidden="true"
                    viewBox="0 0 18 18"
                >
                    <path
                        fill="#4285f4"
                        d="M17.64 9.205c0-.638-.057-1.252-.164-1.841H9v3.481h4.844a4.14 4.14 0 0 1-1.797 2.715v2.259h2.909c1.702-1.567 2.684-3.874 2.684-6.614Z"
                    />
                    <path
                        fill="#34a853"
                        d="M9 18c2.43 0 4.467-.806 5.956-2.181l-2.909-2.259c-.806.54-1.836.859-3.047.859-2.344 0-4.328-1.585-5.037-3.715H.956v2.332A9 9 0 0 0 9 18Z"
                    />
                    <path
                        fill="#fbbc05"
                        d="M3.963 10.704A5.41 5.41 0 0 1 3.682 9c0-.592.102-1.167.281-1.704V4.964H.956A9 9 0 0 0 0 9c0 1.452.347 2.827.956 4.036l3.007-2.332Z"
                    />
                    <path
                        fill="#ea4335"
                        d="M9 3.581c1.321 0 2.507.454 3.441 1.346l2.581-2.581C13.463.892 11.426 0 9 0A9 9 0 0 0 .956 4.964l3.007 2.332C4.672 5.166 6.656 3.581 9 3.581Z"
                    />
                </svg>
                <span>Add as a preferred source on Google</span>
            </a>
        </div>
    </aside>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'

import { preparePreferredSource } from '~/utils/preferred-sources'

const props = defineProps({
    publicationName: {
        type: String,
        required: true,
    },
    publicationUrl: {
        type: String,
        required: true,
    },
})

const emit = defineEmits(['click', 'error', 'fallbackClick', 'view'])
const root = ref(null)
const hasFailed = ref(false)
const isReady = ref(false)
let observer
let hasBeenViewed = false
let isMounted = false
let preferredSourceApi

const publicationDomain = computed(() => {
    try {
        return new URL(props.publicationUrl).hostname
    } catch {
        return props.publicationUrl
    }
})

const deeplink = computed(() => `https://www.google.com/preferences/source?q=${encodeURIComponent(publicationDomain.value)}`)

async function prepare() {
    if (hasBeenViewed) {
        return
    }

    hasBeenViewed = true
    emit('view')

    try {
        const api = await preparePreferredSource()

        if (isMounted) {
            preferredSourceApi = api
            isReady.value = true
        }
    } catch {
        if (isMounted) {
            hasFailed.value = true
            emit('error', 'script_load')
        }
    }
}

function openPreferredSources() {
    if (!preferredSourceApi) {
        return
    }

    emit('click')

    try {
        preferredSourceApi.addPreferredSource()
    } catch {
        hasFailed.value = true
        emit('error', 'flow_open')
    }
}

onMounted(() => {
    isMounted = true

    if (!('IntersectionObserver' in window)) {
        prepare()
        return
    }

    observer = new IntersectionObserver((entries) => {
        if (entries.some(entry => entry.isIntersecting)) {
            observer.disconnect()
            prepare()
        }
    }, { rootMargin: '200px 0px' })

    observer.observe(root.value)
})

onBeforeUnmount(() => {
    isMounted = false
    observer?.disconnect()
})
</script>

<style lang="scss">
@use 'preferred-source-cta';
</style>
